import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authConfig, getAuthorizeEndpoint, getLogoutEndpoint } from '../config/auth';
import { environment } from '../config/environment';
import { claimFile, getOwnProfile } from '../services/api';
import { trackEvent } from '../services/analytics';

interface User {
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  [key: string]: any;
}

interface EmailConsent {
  marketing: boolean;
  product: boolean;
  expiration: boolean;
  consentDate?: string;
}

type AuthStatus = 'idle' | 'loading' | 'authenticating' | 'ready';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  signIn: () => void;
  signOut: () => void;
  loading: boolean;
  authStatus: AuthStatus;
  getValidToken: (forceRefresh?: boolean) => Promise<string | null>;
  getUserId: () => string | null;
  isProUser: () => boolean;
  isBetaUser: () => boolean;
  isAdmin: () => boolean;
  isTestUser: () => boolean;
  getEmailConsent: () => EmailConsent;
  updateEmailConsent: (marketing: boolean, product: boolean, expiration: boolean) => Promise<boolean>;
  hasEmailConsent: () => boolean;
  getSubscriptionStatus: () => string | null;
  getStripeCustomerId: () => string | null;
  isSubscriptionActive: () => boolean;
  // Display name sourced from the user-profiles table (public_name), falling back
  // to email — NEVER the Cognito pool `name` (unreliable for non-OAuth signups).
  getDisplayName: () => string;
  refreshProfile: () => Promise<void>;
  getPublicHandle: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  // public_name from the user-profiles table (display-name source). null = not loaded / none set.
  const [profilePublicName, setProfilePublicName] = useState<string | null>(null);
  // Live public handle (only set when the profile is enabled). null = no live public page.
  const [profileHandle, setProfileHandle] = useState<string | null>(null);

  const getQueryParam = (name: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  };

  const exchangeCodeForToken = useCallback(async (code: string) => {
    const body = JSON.stringify({
      code,
      redirect_uri: authConfig.redirectUri
    });

    try {
      const resp = await fetch(`${environment.api.bff}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        credentials: 'include',
        body
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        console.error('Token exchange failed:', resp.status, errorText);
        throw new Error(`Token exchange failed: ${resp.status} ${errorText}`);
      }

      const data = await resp.json();
      return data;
    } catch (error) {
      console.error('Token exchange error:', error);
      throw error;
    }
  }, []);

  const decodeJwt = useCallback((token: string): User => {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  }, []);

  const processPendingClaims = useCallback(async (idToken: string) => {
    try {
      // Get pending claims from localStorage
      const storedClaims = localStorage.getItem('pendingClaims');
      if (!storedClaims) {
        return { claimed: 0, failed: 0 };
      }

      const claims = JSON.parse(storedClaims);
      if (!Array.isArray(claims) || claims.length === 0) {
        return { claimed: 0, failed: 0 };
      }

      console.log(`Processing ${claims.length} pending claim(s)...`);

      // Process all claims in parallel. Each callback catches its own error and
      // returns a result, so every promise fulfills — the filename is carried
      // through so downstream UI (the "organize into a collection" prompt) can
      // name the collection without re-fetching.
      const results = await Promise.allSettled(
        claims.map(async (claim: any) => {
          try {
            await claimFile(claim.fileId, claim.claimToken, idToken);
            console.log(`Successfully claimed file: ${claim.fileId}`);
            return { success: true, id: claim.fileId, name: claim.fileName as string };
          } catch (error) {
            console.error(`Failed to claim file ${claim.fileId}:`, error);
            return { success: false, id: claim.fileId, name: claim.fileName as string };
          }
        })
      );

      const claimedFiles = results
        .filter(
          (r): r is PromiseFulfilledResult<{ success: boolean; id: string; name: string }> =>
            r.status === 'fulfilled' && r.value.success
        )
        .map((r) => ({ id: r.value.id, name: r.value.name }));
      const claimedCount = claimedFiles.length;
      const failedCount = claims.length - claimedCount;

      // Emit `claim_completed` exactly once per session, after all parallel
      // claims have settled — only if at least one claim succeeded. This
      // locks the per-session unit-of-measure for the L2 funnel (shown →
      // clicked → completed). Decision: 2026-05-20, issue #88.
      if (claimedCount > 0) {
        trackEvent('claim_completed', async () => idToken);
      }

      // Clear pending claims from localStorage
      localStorage.removeItem('pendingClaims');

      // Store claimed files for highlight animation
      if (claimedCount > 0) {
        // Store immediately (no confetti, no toast - just visual highlight)
        localStorage.setItem('recentlyClaimedFiles', JSON.stringify({
          timestamp: Date.now(),
          count: claimedCount
        }));
        console.log(`[Claim] Stored ${claimedCount} claimed files for highlight animation`);
      }

      // When 2+ files were claimed in one shot, surface the "group these into a
      // collection" prompt on Home (ClaimedFilesCollectionPrompt). Kept in a
      // separate key from recentlyClaimedFiles — that one self-destructs after
      // the 10s highlight, but this decision should survive long enough for the
      // user to act (24h freshness window enforced reader-side).
      if (claimedFiles.length >= 2) {
        localStorage.setItem('pendingCollectionPrompt', JSON.stringify({
          timestamp: Date.now(),
          files: claimedFiles,
        }));
        // Notify any mounted listener (Home's useClaimedFilesPrompt). On the
        // no-reload sign-in path, setUser() flips isAuthenticated true BEFORE
        // this write lands, so a one-shot read on auth-change misses the key.
        // This event lets the hook re-read the instant the key is written.
        // Event name kept in sync with ClaimedFilesCollectionPrompt.
        window.dispatchEvent(new Event('pendingcollectionprompt'));
      }

      console.log(`Claim processing complete: ${claimedCount} claimed, ${failedCount} failed`);
      return { claimed: claimedCount, failed: failedCount };
    } catch (error) {
      console.error('Error processing pending claims:', error);
      return { claimed: 0, failed: 0 };
    }
  }, []);

  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const decoded = decodeJwt(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp ? decoded.exp < currentTime : true;
    } catch {
      return true;
    }
  }, [decodeJwt]);

  const isTokenExpiringSoon = useCallback((token: string, bufferMinutes: number = 5): boolean => {
    try {
      const decoded = decodeJwt(token);
      const currentTime = Date.now() / 1000;
      const bufferSeconds = bufferMinutes * 60;
      return decoded.exp ? decoded.exp < (currentTime + bufferSeconds) : true;
    } catch {
      return true;
    }
  }, [decodeJwt]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch(`${environment.api.bff}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token refresh failed:', response.status, errorText);
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();
      const newIdToken = data.id_token;

      if (!newIdToken) {
        console.error('No id_token in refresh response');
        throw new Error('No id_token returned from refresh');
      }

      localStorage.setItem('idToken', newIdToken);

      const decodedUser = data.user;
      setUser(decodedUser);

      return newIdToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Don't immediately sign out - let the caller decide how to handle
      // This allows fallback to current token if it's still valid
      return null;
    }
  }, []);

  const getValidToken = useCallback(async (forceRefresh: boolean = false): Promise<string | null> => {
    const storedToken = localStorage.getItem('idToken');

    if (!storedToken) {
      return null;
    }

    // Force refresh if requested (e.g., after subscription change)
    if (forceRefresh) {
      console.log('Forcing token refresh...');
      return await refreshToken();
    }

    // Check if token is expired or expiring soon
    if (isTokenExpired(storedToken)) {
      console.log('Token is expired, attempting refresh...');
      return await refreshToken();
    }

    if (isTokenExpiringSoon(storedToken)) {
      console.log('Token is expiring soon, attempting refresh...');
      const newToken = await refreshToken();
      return newToken || storedToken; // Fallback to current token if refresh fails
    }

    return storedToken;
  }, [refreshToken, isTokenExpired, isTokenExpiringSoon]);

  const getUserId = useCallback((): string | null => {
    if (!user) {
      return null;
    }
    return user.sub || null;
  }, [user]);

  const isProUser = useCallback((): boolean => {
    if (!user) {
      return false;
    }
    const plan = user.plan || user['custom:plan'] || 'free';
    // Pro features available to both "pro" and "beta" (legacy) users
    return plan === 'pro' || plan === 'beta';
  }, [user]);

  const isBetaUser = useCallback((): boolean => {
    if (!user) {
      return false;
    }
    const plan = user.plan || user['custom:plan'] || 'free';
    // Support both single plan "beta" and comma-separated plans "beta,pro"
    return plan.includes('beta');
  }, [user]);

  const isAdmin = (): boolean => {
    if (!user) {
      return false;
    }
    const isAdminAttr = user['custom:isAdmin'] || 'false';
    return isAdminAttr === 'true';
  };

  const isTestUser = (): boolean => {
    if (!user) {
      return false;
    }
    const testuser = user['custom:testuser'] || 'false';
    return testuser === 'true';
  };

  const getEmailConsent = (): EmailConsent => {
    if (!user) {
      return { marketing: false, product: false, expiration: false };
    }

    const marketingConsent = user['custom:email_marketing'] || 'false';
    const productConsent = user['custom:email_product'] || 'false';
    const expirationConsent = user['custom:email_expiration'] || 'false';
    const consentDate = user['custom:email_date'];

    return {
      marketing: marketingConsent === 'true',
      product: productConsent === 'true',
      expiration: expirationConsent === 'true',
      consentDate: consentDate || undefined
    };
  };

  const hasEmailConsent = (): boolean => {
    if (!user) {
      return false;
    }
    // Check if consent date exists - if it does, user has already given consent
    const consentDate = user['custom:email_date'];
    return !!consentDate;
  };

  const getSubscriptionStatus = (): string | null => {
    if (!user) {
      return null;
    }
    return user.subscription_status || user['custom:subscription_status'] || null;
  };

  const getStripeCustomerId = (): string | null => {
    if (!user) {
      return null;
    }
    return user.stripe_customer_id || user['custom:stripe_customer_id'] || null;
  };

  const isSubscriptionActive = (): boolean => {
    const status = getSubscriptionStatus();
    return status === 'active' || status === 'trialing';
  };

  const updateEmailConsent = async (marketing: boolean, product: boolean, expiration: boolean): Promise<boolean> => {
    try {
      const token = await getValidToken();
      if (!token) {
        throw new Error('No valid token available');
      }

      const userStorageUrl = process.env.REACT_APP_USER_STORAGE_URL;
      if (!userStorageUrl) {
        throw new Error('USER_STORAGE_URL not configured');
      }

      const response = await fetch(`${userStorageUrl}/email-consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          marketing_consent: marketing,
          product_consent: product,
          expiration_consent: expiration
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update consent: ${response.status}`);
      }

      const data = await response.json();

      // Update local user state with new consent values
      if (user) {
        setUser({
          ...user,
          'custom:email_marketing': String(data.marketing_consent),
          'custom:email_product': String(data.product_consent),
          'custom:email_expiration': String(data.expiration_consent ?? expiration),
          'custom:email_date': data.consent_date
        });
      }

      return true;
    } catch (error) {
      console.error('Failed to update email consent:', error);
      return false;
    }
  };

  const signIn = () => {
    // Save current location to return to after authentication
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('authReturnPath', currentPath);
    window.location.href = getAuthorizeEndpoint();
  };

  const signOut = async () => {
    // Clear local state and storage
    setUser(null);
    localStorage.removeItem('idToken');
    localStorage.removeItem('authReturnPath');

    try {
      // Call backend logout endpoint for proper session cleanup
      await fetch(`${environment.api.bff}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include'
      });
    } catch (error) {
      console.error('Backend logout failed, but continuing:', error);
    }

    // Redirect to Cognito logout to clear session cookies
    // This allows users to log in as a different user
    window.location.href = getLogoutEndpoint();
  };

  const initializeFromStoredToken = useCallback(async () => {
    const storedToken = localStorage.getItem('idToken');

    if (storedToken) {
      const expired = isTokenExpired(storedToken);

      if (!expired) {
        try {
          const decodedUser = decodeJwt(storedToken);
          setUser(decodedUser);
          setLoading(false);

          // Process pending claims from anonymous uploads (async, non-blocking)
          // Check if there are any pending claims before processing
          const pendingClaims = localStorage.getItem('pendingClaims');
          if (pendingClaims) {
            processPendingClaims(storedToken).catch(error => {
              console.error('Failed to process pending claims on init:', error);
              // Non-critical error - don't block app initialization
            });
          }

          return true;
        } catch (e) {
          console.error("Failed to decode stored token", e);
          localStorage.removeItem('idToken');
        }
      } else {
        // id_token expired, but the HttpOnly refresh cookie may still be valid (30d).
        // Try a refresh before declaring the session dead.
        const refreshed = await refreshToken();
        if (refreshed) {
          setLoading(false);
          return true;
        }
        localStorage.removeItem('idToken');
      }
    }
    return false;
  }, [isTokenExpired, decodeJwt, processPendingClaims, refreshToken]);

  const handleOAuthCallback = useCallback(async () => {
    const code = getQueryParam("code");
    console.log('[AuthContext] handleOAuthCallback - code param:', code ? 'FOUND' : 'NOT FOUND');

    if (code) {
      console.log('[AuthContext] OAuth callback DETECTED - showing overlay');
      // Set status to authenticating to show loading overlay
      const startTime = Date.now();
      setAuthStatus('authenticating');

      // Give React time to render the overlay before starting token exchange
      // Use a small delay to ensure the overlay is visible before the async work begins
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('[AuthContext] Starting token exchange...');

      window.history.replaceState({}, document.title, authConfig.redirectUri);

      try {
        const data = await exchangeCodeForToken(code);
        const idToken = data.id_token;

        if (!idToken) {
          console.error('No id_token in response:', data);
          throw new Error("No id_token returned");
        }

        const decodedUser = decodeJwt(idToken);

        localStorage.setItem('idToken', idToken);
        setUser(decodedUser);

        // Process pending claims from anonymous uploads. Awaited so the
        // claim PATCH lands before we redirect to /player/<id>; otherwise
        // the player page races the claim and renders as non-owner.
        try {
          await processPendingClaims(idToken);
        } catch (error) {
          console.error('Failed to process pending claims:', error);
          // non-critical - continue with redirect
        }

        // Flush the claim-CTA click flag now that we have a JWT.
        if (localStorage.getItem('claimSigninClicked')) {
          localStorage.removeItem('claimSigninClicked');
          trackEvent('claim_signin_clicked', async () => idToken);
        }

        // Ensure overlay is visible for at least 1000ms for better UX
        const elapsed = Date.now() - startTime;
        const minDisplayTime = 1000;
        if (elapsed < minDisplayTime) {
          await new Promise(resolve => setTimeout(resolve, minDisplayTime - elapsed));
        }

        // Check if we need to redirect before setting status to ready
        const returnPath = localStorage.getItem('authReturnPath');
        const needsRedirect = returnPath && returnPath !== '/' && returnPath !== '';

        if (needsRedirect) {
          // Keep overlay visible and redirect immediately
          // The overlay will persist during the page navigation
          localStorage.removeItem('authReturnPath');
          window.location.href = returnPath;
          // Don't set authStatus to 'ready' - let the new page load with overlay visible
        } else {
          // No redirect needed - fade out normally
          setAuthStatus('ready');
        }

        return true;
      } catch (e) {
        console.error("OAuth callback failed", e);
        setUser(null);

        // Still ensure minimum display time even on error
        const elapsed = Date.now() - startTime;
        const minDisplayTime = 1000;
        if (elapsed < minDisplayTime) {
          await new Promise(resolve => setTimeout(resolve, minDisplayTime - elapsed));
        }

        setAuthStatus('ready');

        // Wait for exit animation to complete (300ms)
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    return false;
  }, [exchangeCodeForToken, decodeJwt, processPendingClaims]);

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      // Skip auto-login on logout success page
      if (window.location.pathname === '/logout-success') {
        setUser(null);
        localStorage.removeItem('idToken');
        setLoading(false);
        setAuthStatus('ready');
        return;
      }

      // Try to initialize from stored token
      if (await initializeFromStoredToken()) {
        setAuthStatus('ready');
        return;
      }

      // Handle OAuth callback if present
      await handleOAuthCallback();

      setLoading(false);
      if (authStatus !== 'authenticating') {
        setAuthStatus('ready');
      }
    };

    initializeAuth();
  }, [initializeFromStoredToken, handleOAuthCallback, authStatus]);

  // Load the user's profile-table public_name once per session (display-name source).
  const refreshProfile = useCallback(async (): Promise<void> => {
    try {
      const token = await getValidToken();
      if (!token) return;
      const p = await getOwnProfile(token);
      // Only surface the public_name / handle while the profile is live.
      setProfilePublicName(p.enabled && p.public_name ? p.public_name : null);
      setProfileHandle(p.enabled && p.handle ? p.handle : null);
    } catch {
      // Endpoint unavailable / no profile — fall back to email; never the pool name.
    }
  }, [getValidToken]);

  useEffect(() => {
    if (user) {
      refreshProfile();
    } else {
      setProfilePublicName(null);
      setProfileHandle(null);
    }
    // Re-run when the user identity changes (sub), not on every token refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]);

  // Display name: profile-table public_name, else email. NEVER the Cognito pool `name`.
  const getDisplayName = useCallback((): string => {
    return profilePublicName || user?.email || 'User';
  }, [profilePublicName, user]);

  // The user's live public handle, or null when there's no published public page.
  const getPublicHandle = useCallback((): string | null => profileHandle, [profileHandle]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      signIn,
      signOut,
      loading,
      authStatus,
      getValidToken,
      getUserId,
      isProUser,
      isBetaUser,
      isAdmin,
      isTestUser,
      getEmailConsent,
      updateEmailConsent,
      hasEmailConsent,
      getSubscriptionStatus,
      getStripeCustomerId,
      isSubscriptionActive,
      getDisplayName,
      refreshProfile,
      getPublicHandle
    }}>
      {children}
    </AuthContext.Provider>
  );
};